import {
  eingabeGueltig,
  kannErhoehen,
  kannVerringern,
  MENGE_MAX,
  type MengenEingabe,
} from '../mengenfeld';
import { ZIFFERN_TASTEN, type ZiffernTaste } from '../ziffern';

/**
 * Tastenfeld des Ziffernblocks (7–9, 4–6, 1–3, 00, 0, ⌫) – Bar-Dialog V2 und Trinkgeld (Cent von
 * rechts). Ohne Zusatzangaben entsteht genau das bisherige Markup. Die Vorwahl und „Als Betrag
 * buchen“ nutzen eine eigene Variante mit Komma (`Vorwahl.tsx`, O3) – diese Tasten bleiben.
 */
export function ZiffernTasten(props: {
  onTaste: (t: ZiffernTaste) => void;
  /** Alle Tasten gesperrt (z. B. während der Verarbeitung). */
  gesperrt?: boolean;
  /** Zusätzliche Klasse am Tastenfeld. */
  klasse?: string;
  /** Beschriftung der Tastengruppe für Hilfstechnik (setzt role="group"). */
  beschriftung?: string;
  /** Fokus beim Antippen nicht vom Eingabefeld nehmen (Enter bestätigt dann weiter das Feld). */
  fokusHalten?: boolean;
}) {
  const { onTaste, gesperrt, klasse, beschriftung, fokusHalten } = props;
  return (
    <div
      className={klasse === undefined ? 'ziffern-tasten' : `ziffern-tasten ${klasse}`}
      {...(beschriftung === undefined ? {} : { role: 'group', 'aria-label': beschriftung })}
    >
      {ZIFFERN_TASTEN.map((t) => (
        <button
          key={t}
          type="button"
          {...(gesperrt === undefined ? {} : { disabled: gesperrt })}
          {...(fokusHalten === true
            ? {
                onMouseDown: (e: { preventDefault: () => void }) => {
                  e.preventDefault();
                },
              }
            : {})}
          onClick={() => {
            onTaste(t);
          }}
          aria-label={t === '⌫' ? 'Letzte Ziffer löschen' : t}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/**
 * MB2-Mengenfeld der freien Position (O10: unverändert im Betragseditor „Als Betrag buchen“;
 * Komponente `MB2/Mengenfeld`, Zustände Normal,
 * Direkteingabe, Untergrenze, Obergrenze, Ungültig): [−] und [+] je 52 px, der Wert ist
 * antippbar und startet bzw. übernimmt die Direkteingabe.
 */
export function Mengenfeld(props: {
  menge: number;
  eingabe: MengenEingabe | null;
  wertKnopf: { current: HTMLButtonElement | null };
  onVerringern: () => void;
  onErhoehen: () => void;
  onWert: () => void;
}) {
  const { menge, eingabe } = props;
  const ungueltig = eingabe !== null && !eingabeGueltig(eingabe);
  const zustand = eingabe === null ? '' : ungueltig ? ' eingabe ungueltig' : ' eingabe';
  return (
    <div className="mengenfeld" role="group" aria-label="Menge">
      <span className="dlg-feldname" aria-hidden="true">
        {eingabe === null
          ? 'Menge (Stück)'
          : ungueltig
            ? `Menge 1–${String(MENGE_MAX)} eingeben`
            : 'Menge – Ziffernblock tippt hier'}
      </span>
      <div className={`mengenfeld-stepper${zustand}`}>
        <button
          type="button"
          aria-label="Menge verringern"
          disabled={eingabe === null && !kannVerringern(menge)}
          onClick={props.onVerringern}
        >
          −
        </button>
        <button
          ref={(el) => {
            props.wertKnopf.current = el;
          }}
          type="button"
          className="mengenfeld-wert"
          aria-pressed={eingabe !== null}
          aria-label={
            eingabe === null
              ? `Menge ${String(menge)} – antippen zum Eingeben`
              : `Menge eingeben: ${eingabe.text === '' ? 'leer' : eingabe.text}`
          }
          onClick={props.onWert}
        >
          <span className="mengenfeld-zahl">
            {eingabe === null ? (
              <>
                {menge}
                <span className="mengenfeld-mal">×</span>
              </>
            ) : (
              <>
                {eingabe.text}
                <span className="mengenfeld-cursor" aria-hidden="true" />
              </>
            )}
          </span>
          <span className="mengenfeld-hinweis" aria-hidden="true">
            {eingabe !== null
              ? `1–${String(MENGE_MAX)}`
              : kannErhoehen(menge)
                ? 'antippen'
                : 'höchstens'}
          </span>
        </button>
        <button
          type="button"
          aria-label="Menge erhöhen"
          disabled={eingabe === null && !kannErhoehen(menge)}
          onClick={props.onErhoehen}
        >
          +
        </button>
      </div>
    </div>
  );
}
